/**
 * Module Google Analytics 4 (GA4) - Événements et Conversions
 *
 * Fonctionnalités :
 * - Helper global window.sountsouTrackEvent(eventName, params) pour envoyer des événements GA4
 * - Capture automatique des conversions : formulaires, liens d'appels (tel:), emails (mailto:),
 *   téléchargements de documents et partages sur les réseaux sociaux.
 * - Respect des signaux Google Consent Mode v2 et Vanilla CookieConsent.
 *
 * @package Sountsou2026
 */

/**
 * Envoie un événement personnalisé à Google Analytics 4
 *
 * @param {string} eventName Nom de l'événement GA4 (ex: 'generate_lead', 'share', etc.)
 * @param {Object} eventParams Paramètres de l'événement
 */
export function trackEvent(eventName, eventParams = {}) {
    window.dataLayer = window.dataLayer || [];

    if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, eventParams);
    } else {
        window.dataLayer.push({
            event: eventName,
            ...eventParams,
        });
    }
}

// Exposition globale pour faciliter l'intégration avec d'autres scripts ou inline templates
if (typeof window !== 'undefined') {
    window.sountsouTrackEvent = trackEvent;
}

/**
 * Initialise les écouteurs d'événements automatiques
 */
export function initAnalytics() {
    // Vérification de l'environnement navigateur et exclusion de l'admin WordPress
    if (typeof document === 'undefined' || (document.body && document.body.classList.contains('wp-admin'))) {
        return;
    }

    const fileExtensionRegex = /\.(pdf|docx?|xlsx?|pptx?|zip|rar|tar|gz|csv)$/i;

    // Délégation d'événements pour capturer les clics sur les liens interactifs
    document.addEventListener('click', (event) => {
        const link = event.target.closest('a');
        if (!link || !link.href) {
            return;
        }

        const href = link.href.trim();

        // 1. Clic sur un lien téléphonique (tel:)
        if (href.startsWith('tel:')) {
            trackEvent('click_telephone', {
                event_category: 'Contact',
                event_label: href.replace(/^tel:/i, ''),
                value: 1,
            });
            return;
        }

        // 2. Clic sur un lien email (mailto:)
        if (href.startsWith('mailto:')) {
            trackEvent('click_email', {
                event_category: 'Contact',
                event_label: href.replace(/^mailto:/i, '').split('?')[0],
                value: 1,
            });
            return;
        }

        // 3. Téléchargement de document (fichiers PDF, Word, Excel, etc.)
        const urlPath = link.pathname || '';
        const matchFile = urlPath.match(fileExtensionRegex);
        if (matchFile) {
            const fileName = urlPath.split('/').pop();
            const fileExtension = matchFile[1].toLowerCase();
            trackEvent('file_download', {
                event_category: 'Download',
                file_name: fileName,
                file_extension: fileExtension,
                link_url: href,
            });
            return;
        }

        // 4. Clic sur un bouton de partage social
        if (link.closest('.post-share') || link.classList.contains('post-share__link')) {
            const network = link.dataset.network || link.getAttribute('aria-label') || 'social';
            trackEvent('share', {
                method: network,
                content_type: 'article',
                item_id: window.location.pathname,
            });
            return;
        }

        // 5. Clic sortant (Outbound external link)
        if (
            link.hostname &&
            link.hostname !== window.location.hostname &&
            !href.startsWith('javascript:') &&
            !href.startsWith('#')
        ) {
            trackEvent('click', {
                event_category: 'Outbound',
                outbound: true,
                link_domain: link.hostname,
                link_url: href,
                transport_type: 'beacon',
            });
        }
    }, { passive: true });
}
